import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Retainer } from '../../types';
import apiService from '../../services/api';

interface RetainersState {
  retainers: Retainer[];
  currentRetainer: Retainer | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  filters: {
    status: string;
    clientId: string;
    search: string;
  };
  stats: {
    totalRetainers: number;
    activeRetainers: number;
    totalMonthlyValue: number;
    totalMonthlyHours: number;
    avgHourlyRate: number;
  } | null;
  workloadAnalysis: {
    [retainerId: string]: {
      retainerId: string;
      retainerName: string;
      monthlyHours: number;
      workloadModel: any;
    };
  };
}

const initialState: RetainersState = {
  retainers: [],
  currentRetainer: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  },
  filters: {
    status: 'all',
    clientId: 'all',
    search: '',
  },
  stats: null,
  workloadAnalysis: {},
};

// Async thunks
export const fetchRetainers = createAsyncThunk(
  'retainers/fetchRetainers',
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getRetainers(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch retainers');
    }
  }
);

export const fetchRetainer = createAsyncThunk(
  'retainers/fetchRetainer',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiService.getRetainer(id);
      return response.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch retainer');
    }
  }
);

export const createRetainer = createAsyncThunk(
  'retainers/createRetainer',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await apiService.createRetainer(data);
      return response.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create retainer');
    }
  }
);

export const updateRetainer = createAsyncThunk(
  'retainers/updateRetainer',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateRetainer(id, data);
      return response.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update retainer');
    }
  }
);

export const deleteRetainer = createAsyncThunk(
  'retainers/deleteRetainer',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiService.deleteRetainer(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete retainer');
    }
  }
);

export const fetchRetainerStats = createAsyncThunk(
  'retainers/fetchRetainerStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getRetainerStats();
      return response.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch retainer stats');
    }
  }
);

export const fetchRetainerWorkloadAnalysis = createAsyncThunk(
  'retainers/fetchRetainerWorkloadAnalysis',
  async ({ id, months }: { id: string; months?: number }, { rejectWithValue }) => {
    try {
      const response = await apiService.getRetainerWorkloadAnalysis(id, months);
      return response.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch workload analysis');
    }
  }
);

const retainersSlice = createSlice({
  name: 'retainers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: 'all',
        clientId: 'all',
        search: '',
      };
    },
    setCurrentRetainer: (state, action) => {
      state.currentRetainer = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch retainers
      .addCase(fetchRetainers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRetainers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.retainers = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchRetainers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch single retainer
      .addCase(fetchRetainer.fulfilled, (state, action) => {
        state.currentRetainer = action.payload as Retainer;
      })
      // Create retainer
      .addCase(createRetainer.fulfilled, (state, action) => {
        state.retainers.push(action.payload as Retainer);
      })
      // Update retainer
      .addCase(updateRetainer.fulfilled, (state, action) => {
        const updatedRetainer = action.payload as Retainer;
        const index = state.retainers.findIndex(retainer => retainer.id === updatedRetainer.id);
        if (index !== -1) {
          state.retainers[index] = updatedRetainer;
        }
        if (state.currentRetainer?.id === updatedRetainer.id) {
          state.currentRetainer = updatedRetainer;
        }
      })
      // Delete retainer
      .addCase(deleteRetainer.fulfilled, (state, action) => {
        state.retainers = state.retainers.filter(retainer => retainer.id !== action.payload);
        if (state.currentRetainer?.id === action.payload) {
          state.currentRetainer = null;
        }
      })
      // Fetch retainer stats
      .addCase(fetchRetainerStats.fulfilled, (state, action) => {
        state.stats = action.payload.overview;
      })
      // Fetch workload analysis
      .addCase(fetchRetainerWorkloadAnalysis.fulfilled, (state, action) => {
        const data = action.payload;
        state.workloadAnalysis[data.retainerId] = data;
      });
  },
});

export const { clearError, setFilters, clearFilters, setCurrentRetainer } = retainersSlice.actions;
export default retainersSlice.reducer;
